import React, { useState, useRef, useEffect } from 'react';
import {
  Accordion,
  AccordionItem,
  Flex,
  InfoIcon,
  Table,
  Tbody,
  Tr,
  Td,
  chakra as C,
} from '@tapresearch/ui/components';

import ReactTooltip from 'Shared/Tooltip';
import useAsset from '../../hook/useAsset';
import Modal from 'Shared/Modal';
import * as S from './BarChart.styled';

export default React.memo(
  ({ isCrossTab, data, id, mode, crossTabQuestion, isRating }) => {
    const [isOpenFullAnswerImageURL, setIsOpenFullAnswerImageURL] =
      useState(null);
    const asset = useAsset();
    const [graphData, setGraphData] = useState();
    const toolTipId = useRef(`toolTipId-${id}`);
    const [rankFilter, setRankFilter] = useState(undefined);

    const isCTRanking =
      crossTabQuestion &&
      crossTabQuestion.answer_type === asset.constants.TAP_ANSWER_TYPE_RANKING;

    const processRankingData = (sets) => {
      let total = 0;
      const datasets = sets.map((question) => {
        let qCount = 0;
        const newVals = question.values.map(({ id, label, primary_ranks }) => {
          const aCount = primary_ranks.reduce(
            (a, b, i) => (rankFilter >= i ? a + b : a),
            0
          );
          qCount += aCount;
          return { id, label, total: aCount, primary_ranks };
        });
        total += qCount;
        return { ...question, total: qCount, values: newVals };
      });
      return { datasets, total };
    };

    useEffect(() => {
      if (isCrossTab) {
        const { datasets, total } =
          (isCTRanking ? processRankingData(data.datasets) : data) || [];
        setGraphData({
          legends: datasets[0]
            ? datasets[0].values.reduce(
                (acc, elem) => ({ ...acc, [elem.label]: true }),
                {}
              )
            : [],
          secondaryAnswers: data.secondaryAnswers,
          datasets,
          total,
        });
      } else {
        setGraphData(data);
      }
    }, [data, isCTRanking, rankFilter, isCrossTab, mode]);

    useEffect(() => {
      setRankFilter(isCTRanking ? 0 : undefined);
    }, [isCTRanking]);

    useEffect(() => {
      ReactTooltip.rebuild();
    }, [graphData]);

    const onChangeLegendsFilter = (field) => {
      const newLegends = {
        ...graphData.legends,
        [field]: !graphData.legends[field],
      };

      setGraphData({ ...graphData, legends: newLegends });
    };

    const onChangeRanksFilter = (field) => {
      setRankFilter(field);
    };

    if (!graphData || (isCrossTab && !graphData.legends)) {
      return null;
    }

    // Find row that is longest in term of width
    const highestTotal = isCrossTab
      ? graphData.datasets.reduce(
          (curr, elem) => (elem.total > curr ? elem.total : curr),
          0
        )
      : graphData.datasets.reduce(
          (curr, elem) => (elem.total > curr ? elem.total : curr),
          0
        );
    return (
      <div>
        {isCrossTab ? (
          <div>
            {graphData.datasets.map((dataset, idx) => {
              return (
                <ChartRow
                  rowIndex={idx}
                  key={idx}
                  dataset={dataset}
                  legends={graphData.legends}
                  isCrossTab
                  toolTipId={toolTipId.current}
                  mode={mode}
                  total={graphData.total}
                  highestTotal={highestTotal}
                  isRating={isRating}
                />
              );
            })}
            <S.LegendContainer>
              {isCTRanking && (
                <S.LegendItem>
                  <S.RankHelpTooltip
                    place="bottom"
                    type="dark"
                    effect="solid"
                  />
                  <S.TabSelect
                    onChange={(e) => onChangeRanksFilter(e.target.value)}
                    value={graphData.rankFilter}
                  >
                    {data.datasets.map((_, i) => {
                      return (
                        <option value={i} key={i}>
                          {i === 0
                            ? `#${i + 1} Ranked answers only`
                            : `#1 to #${i + 1} Ranked answers`}
                        </option>
                      );
                    })}
                  </S.TabSelect>
                  <span
                    data-tip={`Range of ranked responses included from: "${crossTabQuestion.name}".`}
                  >
                    <InfoIcon />
                  </span>
                </S.LegendItem>
              )}
              {graphData.datasets &&
                graphData.datasets[0] &&
                graphData.datasets[0].values.map((value, idx) => {
                  return (
                    <S.LegendItem key={idx}>
                      {graphData.secondaryAnswers[idx].image &&
                        graphData.secondaryAnswers[idx].image.thumbnail && (
                          <S.RowThumbImage
                            onClick={() =>
                              setIsOpenFullAnswerImageURL(
                                graphData.secondaryAnswers[idx].image.full
                              )
                            }
                            src={
                              graphData.secondaryAnswers[idx].image.thumbnail
                            }
                          />
                        )}
                      <S.Legend
                        onClick={() => onChangeLegendsFilter(value.label)}
                        background={S.getBarLabelColor(idx)}
                      >
                        <S.LegendIcon
                          src={
                            graphData.legends[value.label]
                              ? asset.check_square_icon
                              : asset.square_regular_icon
                          }
                        />
                        {value.label}
                      </S.Legend>
                    </S.LegendItem>
                  );
                })}
            </S.LegendContainer>
            <S.Tooltip
              id={toolTipId.current}
              place="top"
              type="dark"
              getContent={(dataTip) => {
                if (graphData.datasets[dataTip]) {
                  return (
                    <div>
                      <p>{graphData.datasets[dataTip].label}</p>
                      {isRating && (
                        <p>
                          Average Rating:{' '}
                          {parseFloat(
                            graphData.datasets[dataTip].values.reduce(
                              (acc, ans, i) => (i + 1) * ans.total + acc,
                              0
                            ) / graphData.datasets[dataTip].total
                          ).toFixed(2)}
                        </p>
                      )}
                      {graphData.datasets[dataTip].values.map((value, idx) => {
                        if (graphData.legends[value.label]) {
                          const percentage = parseFloat(
                            (value.total / graphData.datasets[dataTip].total ||
                              0) * 100
                          ).toFixed(2);
                          return (
                            <div key={idx}>
                              {value.label}: {percentage}% ({value.total})
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                }
              }}
            />
          </div>
        ) : (
          <>
            {isRating && (
              <Flex {...S.AverageRatingStyles}>
                Average:{' '}
                {parseFloat(
                  graphData.datasets.reduce(
                    (acc, ans, i) => (i + 1) * ans.total + acc,
                    0
                  ) / graphData.total
                ).toFixed(2)}
              </Flex>
            )}
            {graphData.datasets.map((dataset, idx) => {
              return (
                <ChartRow
                  total={graphData.total}
                  dataset={dataset}
                  key={idx}
                  highestTotal={highestTotal}
                  onViewFullImage={() =>
                    setIsOpenFullAnswerImageURL(dataset.image.full)
                  }
                />
              );
            })}
          </>
        )}
        {isOpenFullAnswerImageURL && (
          <Modal
            maxWidth={800}
            isOpen={true}
            onClose={() => setIsOpenFullAnswerImageURL(null)}
          >
            {isOpenFullAnswerImageURL}
          </Modal>
        )}
      </div>
    );
  }
);

const ChartRow = React.memo(
  ({
    dataset,
    total,
    isCrossTab,
    rowIndex,
    legends,
    toolTipId,
    onViewFullImage,
    mode,
    highestTotal,
  }) => {
    const asset = useAsset();
    const labelRef = useRef();
    const [height, setHeight] = useState(0);

    useEffect(() => {
      if (labelRef.current) {
        setHeight(labelRef.current.clientHeight);
      }
    }, []);

    const percentage = parseFloat((dataset.total / total || 0) * 100).toFixed(
      2
    );
    const barPercentage = parseFloat(
      (dataset.total / highestTotal || 0) * 100
    ).toFixed(2);
    const thumbImage = dataset.image && dataset.image.thumbnail;

    if (isCrossTab) {
      const barPercentage =
        mode === 'percentage'
          ? 100
          : parseFloat((dataset.total / highestTotal || 0) * 100).toFixed(2);
      return (
        <S.Row>
          <S.RowLabel withThumbImage={thumbImage}>{dataset.label}</S.RowLabel>
          <S.RowBar>
            <S.Bar>
              <S.CrossTabRow percentage={barPercentage}>
                {dataset.values.map((value, idx) => {
                  const percentage = parseFloat(
                    (value.total / dataset.total || 0) * 100
                  ).toFixed(2);
                  if (!percentage || percentage <= 0 || !legends[value.label]) {
                    return null;
                  }
                  const bg = S.getBarLabelColor(idx);
                  return (
                    <S.BarItem
                      key={value.id}
                      background={bg}
                      percentage={percentage}
                      data-for={toolTipId}
                      data-tip={rowIndex}
                    >
                      <S.BarItemLabel
                        color={bg}
                        style={{ visibility: 'initial' }}
                      >
                        {mode === 'percentage' ? `${percentage}%` : value.total}
                      </S.BarItemLabel>
                    </S.BarItem>
                  );
                })}
              </S.CrossTabRow>
            </S.Bar>
          </S.RowBar>
        </S.Row>
      );
    }

    const showAccordion =
      dataset.option_type ===
        asset.constants.TAP_OPTION_TYPE_PINNED_WITH_TEXT &&
      dataset.other_answers;

    const renderOtherAnswers = () =>
      showAccordion && (
        <Flex align="flex-start" mb="15px">
          <C.img src={asset.arrow_right_icon} p="10px" />
          <Accordion flexGrow="1">
            <AccordionItem
              itemTitle="View Text Answers"
              _buttonStyle={{ bg: 'gray.100', borderRadius: 'base' }}
            >
              <S.TableContainer>
                <Table>
                  <Tbody>
                    {dataset.other_answers.map((value, i) => (
                      <Tr key={i}>
                        <Td>
                          {i + 1}) {value}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </S.TableContainer>
            </AccordionItem>
          </Accordion>
        </Flex>
      );

    return (
      <>
        <S.Row grayBg>
          {thumbImage && (
            <S.RowThumbImage onClick={onViewFullImage} src={thumbImage} />
          )}
          <S.RowBar grayBg>
            <S.Bar>
              <S.BarItem percentage={percentage}>
                <div style={{ height }} />
              </S.BarItem>
            </S.Bar>
            <S.BarLabel>
              <S.BarLabelContent ref={labelRef}>
                {dataset.label}
              </S.BarLabelContent>
              <S.BarRowLabel>{`${dataset.total} (${percentage}%)`}</S.BarRowLabel>
            </S.BarLabel>
          </S.RowBar>
        </S.Row>
        {renderOtherAnswers()}
      </>
    );
  }
);
